import React, { useState, useEffect, useCallback } from 'react';
import { Home, ArrowUpDown, Wallet, TrendingUp, Menu, X, ExternalLink, Coins, Zap, RefreshCw } from 'lucide-react';

// Mock Web3 functionality for demonstration
const mockWeb3 = {
  isConnected: false,
  account: null,
  chainId: null,
  balance: {
    ETH: '0',
    FIB: '0'
  }
};

const SUPPORTED_CHAINS = {
  1: { name: 'Ethereum', symbol: 'ETH', color: 'bg-blue-500' },
  8453: { name: 'Base', symbol: 'ETH', color: 'bg-blue-600' },
  11155111: { name: 'Sepolia', symbol: 'ETH', color: 'bg-gray-500' },
  42161: { name: 'Arbitrum', symbol: 'ETH', color: 'bg-blue-400' },
  10: { name: 'Optimism', symbol: 'ETH', color: 'bg-red-500' }
};

const LIQUIDITY_POOLS = [
  { id: 1, pair: 'ETH/FIB', apy: '24.5%', tvl: '$1.2M', rewards: 'FIB' },
  { id: 2, pair: 'FIB/USDC', apy: '18.3%', tvl: '$850K', rewards: 'FIB' },
  { id: 3, pair: 'ETH/USDC', apy: '12.7%', tvl: '$2.1M', rewards: 'FIB' }
];

const FlibberApp = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [hasFIBTokens, setHasFIBTokens] = useState(false);
  const [userBalance, setUserBalance] = useState({ ETH: '0.0', FIB: '0' });
  const [selectedChainIn, setSelectedChainIn] = useState(1);
  const [selectedChainOut, setSelectedChainOut] = useState(8453);
  const [slotAmount, setSlotAmount] = useState('');
  const [faucetCooldown, setFaucetCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userSlots, setUserSlots] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Mock wallet connection
  const connectWallet = async () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsWalletConnected(true);
      setUserBalance({ ETH: '1.25', FIB: '0' });
      addNotification('Wallet connected successfully!', 'success');
      setIsLoading(false);
    }, 1500);
  };

  // Mock FIB faucet request
  const requestFIBFaucet = async () => {
    if (faucetCooldown > 0) return;
    
    setIsLoading(true);
    setTimeout(() => {
      setHasFIBTokens(true);
      setUserBalance(prev => ({ ...prev, FIB: '100' }));
      setFaucetCooldown(86400); // 24 hours in seconds
      addNotification('100 FIB tokens received!', 'success');
      setIsLoading(false);
    }, 2000);
  };

  // Add notification system
  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Mock slotting functionality
  const performSlot = async () => {
    if (!slotAmount || parseFloat(slotAmount) <= 0) {
      addNotification('Please enter a valid amount', 'error');
      return;
    }

    setIsLoading(true);
    setTimeout(() => {
      const newSlot = {
        id: Date.now(),
        chainIn: selectedChainIn,
        chainOut: selectedChainOut,
        amountIn: slotAmount,
        amountOut: (parseFloat(slotAmount) * 0.995).toFixed(4), // 0.5% slippage
        status: 'pending',
        timestamp: new Date().toISOString()
      };
      
      setUserSlots(prev => [newSlot, ...prev]);
      setSlotAmount('');
      addNotification('Slot transaction initiated!', 'success');
      setIsLoading(false);
      
      // Simulate completion after 30 seconds
      setTimeout(() => {
        setUserSlots(prev => 
          prev.map(slot => 
            slot.id === newSlot.id 
              ? { ...slot, status: 'completed' }
              : slot
          )
        );
        addNotification('Slot completed successfully!', 'success');
      }, 30000);
    }, 2000);
  };

  // Countdown for faucet cooldown
  useEffect(() => {
    if (faucetCooldown > 0) {
      const timer = setInterval(() => {
        setFaucetCooldown(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [faucetCooldown]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // Navigation component
  const Navigation = () => (
    <nav className="bg-gray-900 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">F</span>
            </div>
            <span className="text-white text-xl font-bold">Flibber</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <NavButton icon={Home} label="Home" page="home" />
            <NavButton icon={ArrowUpDown} label="Slotting" page="slotting" />
            <NavButton icon={Wallet} label="Wallet" page="wallet" />
            <NavButton icon={TrendingUp} label="Yield" page="yield" />
          </div>

          {/* Wallet Connection */}
          <div className="flex items-center space-x-4">
            {isWalletConnected ? (
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-white text-sm font-medium">
                    {userBalance.ETH} ETH
                  </div>
                  <div className="text-purple-400 text-xs">
                    {userBalance.FIB} FIB
                  </div>
                </div>
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
            ) : (
              <button
                onClick={connectWallet}
                disabled={isLoading}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {isLoading ? 'Connecting...' : 'Connect Wallet'}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex justify-around py-4 border-t border-gray-800">
          <NavButton icon={Home} label="Home" page="home" mobile />
          <NavButton icon={ArrowUpDown} label="Slotting" page="slotting" mobile />
          <NavButton icon={Wallet} label="Wallet" page="wallet" mobile />
          <NavButton icon={TrendingUp} label="Yield" page="yield" mobile />
        </div>
      </div>
    </nav>
  );

  const NavButton = ({ icon: Icon, label, page, mobile = false }) => (
    <button
      onClick={() => setCurrentPage(page)}
      className={`flex ${mobile ? 'flex-col' : 'flex-row'} items-center space-${mobile ? 'y' : 'x'}-1 px-3 py-2 rounded-lg transition-colors ${
        currentPage === page
          ? 'text-purple-400 bg-purple-900/20'
          : 'text-gray-400 hover:text-white hover:bg-gray-800'
      }`}
    >
      <Icon size={mobile ? 20 : 18} />
      <span className={`${mobile ? 'text-xs' : 'text-sm'} font-medium`}>{label}</span>
    </button>
  );

  // Notification Component
  const NotificationToast = ({ notification }) => (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
      notification.type === 'success' ? 'bg-green-600' : 
      notification.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
    } text-white`}>
      {notification.message}
    </div>
  );

  // Home Page
  const HomePage = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-3xl mx-auto flex items-center justify-center mb-6 animate-pulse">
              <span className="text-white text-4xl font-bold">F</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
              Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">Flibber</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Cross-chain crypto slotting and DeFi yield farming platform
            </p>
          </div>

          {!isWalletConnected ? (
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto">
              <h2 className="text-2xl font-bold text-white mb-4">Get Started</h2>
              <p className="text-gray-300 mb-6">Connect your wallet to access the Flibber ecosystem</p>
              <button
                onClick={connectWallet}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 text-white py-3 px-6 rounded-lg font-medium transition-all transform hover:scale-105"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="animate-spin" size={20} />
                    <span>Connecting...</span>
                  </div>
                ) : (
                  'Connect Wallet'
                )}
              </button>
            </div>
          ) : !hasFIBTokens ? (
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto">
              <div className="text-center mb-6">
                <Coins className="mx-auto text-purple-400 mb-4" size={48} />
                <h2 className="text-2xl font-bold text-white mb-2">Request FIB Tokens</h2>
                <p className="text-gray-300">Get 100 FIB tokens to unlock the platform and pay for gas fees</p>
              </div>
              
              {faucetCooldown > 0 ? (
                <div className="text-center">
                  <p className="text-yellow-400 mb-4">Faucet cooldown: {formatTime(faucetCooldown)}</p>
                  <button
                    disabled
                    className="w-full bg-gray-600 text-gray-400 py-3 px-6 rounded-lg font-medium cursor-not-allowed"
                  >
                    Faucet Unavailable
                  </button>
                </div>
              ) : (
                <button
                  onClick={requestFIBFaucet}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 text-white py-3 px-6 rounded-lg font-medium transition-all transform hover:scale-105"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="animate-spin" size={20} />
                      <span>Requesting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Zap size={20} />
                      <span>Request 100 FIB</span>
                    </div>
                  )}
                </button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              <FeatureCard
                icon={ArrowUpDown}
                title="Cross-Chain Slotting"
                description="Seamlessly swap assets across different blockchains with our innovative slotting mechanism"
                onClick={() => setCurrentPage('slotting')}
              />
              <FeatureCard
                icon={TrendingUp}
                title="Yield Farming"
                description="Earn rewards by providing liquidity to our high-APY pools"
                onClick={() => setCurrentPage('yield')}
              />
              <FeatureCard
                icon={Wallet}
                title="Portfolio Management"
                description="Track your assets, transactions, and earnings across all chains"
                onClick={() => setCurrentPage('wallet')}
              />
            </div>
          )}
        </div>

        {/* Stats Section */}
        {hasFIBTokens && (
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <StatCard title="Total Value Locked" value="$4.2M" />
            <StatCard title="Active Slots" value="1,247" />
            <StatCard title="Supported Chains" value="5" />
            <StatCard title="FIB Holders" value="3,891" />
          </div>
        )}
      </div>
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, description, onClick }) => (
    <div 
      onClick={onClick}
      className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 hover:bg-gray-800/70 transition-all cursor-pointer transform hover:scale-105"
    >
      <Icon className="text-purple-400 mb-4" size={40} />
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </div>
  );

  const StatCard = ({ title, value }) => (
    <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-4 text-center">
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-gray-400 text-sm">{title}</div>
    </div>
  );

  // Slotting Page
  const SlottingPage = () => (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Cross-Chain Slotting</h1>
          <p className="text-gray-400">Swap assets across different blockchains seamlessly</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Slotting Interface */}
          <div className="bg-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-6">Create New Slot</h2>
            
            {/* From Chain */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-2">From Chain</label>
              <select 
                value={selectedChainIn}
                onChange={(e) => setSelectedChainIn(parseInt(e.target.value))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3"
              >
                {Object.entries(SUPPORTED_CHAINS).map(([id, chain]) => (
                  <option key={id} value={id}>{chain.name}</option>
                ))}
              </select>
            </div>

            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-2">Amount</label>
              <div className="relative">
                <input
                  type="number"
                  value={slotAmount}
                  onChange={(e) => setSlotAmount(e.target.value)}
                  placeholder="0.0"
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 pr-16"
                />
                <div className="absolute right-3 top-3 text-gray-400">
                  {SUPPORTED_CHAINS[selectedChainIn]?.symbol}
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center mb-6">
              <div className="bg-purple-600 rounded-full p-2">
                <ArrowUpDown className="text-white" size={20} />
              </div>
            </div>

            {/* To Chain */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-2">To Chain</label>
              <select 
                value={selectedChainOut}
                onChange={(e) => setSelectedChainOut(parseInt(e.target.value))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3"
              >
                {Object.entries(SUPPORTED_CHAINS).map(([id, chain]) => (
                  <option key={id} value={id}>{chain.name}</option>
                ))}
              </select>
            </div>

            {/* Estimated Output */}
            {slotAmount && (
              <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Estimated Output:</span>
                  <span className="text-white">{(parseFloat(slotAmount) * 0.995).toFixed(4)} {SUPPORTED_CHAINS[selectedChainOut]?.symbol}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Slotting Fee:</span>
                  <span className="text-white">10 FIB</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Slippage:</span>
                  <span className="text-white">0.5%</span>
                </div>
              </div>
            )}

            {/* Slot Button */}
            <button
              onClick={performSlot}
              disabled={!slotAmount || isLoading || !hasFIBTokens}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-medium transition-all"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <RefreshCw className="animate-spin" size={20} />
                  <span>Processing...</span>
                </div>
              ) : (
                'Create Slot'
              )}
            </button>
          </div>

          {/* Recent Slots */}
          <div className="bg-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-6">Your Recent Slots</h2>
            
            {userSlots.length === 0 ? (
              <div className="text-center py-8">
                <ArrowUpDown className="mx-auto text-gray-600 mb-4" size={48} />
                <p className="text-gray-400">No slots created yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userSlots.map((slot) => (
                  <div key={slot.id} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-white font-medium">
                          {SUPPORTED_CHAINS[slot.chainIn]?.name} → {SUPPORTED_CHAINS[slot.chainOut]?.name}
                        </div>
                        <div className="text-gray-400 text-sm">
                          {slot.amountIn} → {slot.amountOut} {SUPPORTED_CHAINS[slot.chainOut]?.symbol}
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs ${
                        slot.status === 'pending' 
                          ? 'bg-yellow-600 text-yellow-100' 
                          : 'bg-green-600 text-green-100'
                      }`}>
                        {slot.status}
                      </div>
                    </div>
                    <div className="text-gray-500 text-xs">
                      {new Date(slot.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Wallet Page
  const WalletPage = () => (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Wallet</h1>
          <p className="text-gray-400">Manage your assets and view transaction history</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Balance Overview */}
          <div className="lg:col-span-2 bg-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-6">Asset Balances</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">ETH</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">Ethereum</div>
                    <div className="text-gray-400 text-sm">ETH</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-medium">{userBalance.ETH} ETH</div>
                  <div className="text-gray-400 text-sm">≈ ${(parseFloat(userBalance.ETH) * 2500).toFixed(2)}</div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">FIB</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">Flibber Token</div>
                    <div className="text-gray-400 text-sm">FIB</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-medium">{userBalance.FIB} FIB</div>
                  <div className="text-gray-400 text-sm">≈ ${(parseFloat(userBalance.FIB) * 0.1).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-6">Quick Actions</h2>
            
            <div className="space-y-4">
              <button 
                onClick={() => setCurrentPage('slotting')}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <ArrowUpDown size={20} />
                <span>New Slot</span>
              </button>
              
              <button 
                onClick={() => setCurrentPage('yield')}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <TrendingUp size={20} />
                <span>Add Liquidity</span>
              </button>
              
              {!hasFIBTokens && faucetCooldown === 0 && (
                <button 
                  onClick={requestFIBFaucet}
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <Zap size={20} />
                  <span>{isLoading ? 'Requesting...' : 'Request FIB'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="mt-8 bg-gray-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-6">Recent Transactions</h2>
          
          {userSlots.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400">No transactions yet</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-gray-400 text-sm border-b border-gray-700">
                    <th className="text-left py-3">Type</th>
                    <th className="text-left py-3">From</th>
                    <th className="text-left py-3">To</th>
                    <th className="text-left py-3">Amount</th>
                    <th className="text-left py-3">Status</th>
                    <th className="text-left py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {userSlots.map((slot) => (
                    <tr key={slot.id} className="border-b border-gray-700">
                      <td className="py-3 text-white">Slot</td>
                      <td className="py-3 text-gray-300">{SUPPORTED_CHAINS[slot.chainIn]?.name}</td>
                      <td className="py-3 text-gray-300">{SUPPORTED_CHAINS[slot.chainOut]?.name}</td>
                      <td className="py-3 text-gray-300">{slot.amountIn} → {slot.amountOut}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          slot.status === 'pending' 
                            ? 'bg-yellow-600 text-yellow-100' 
                            : 'bg-green-600 text-green-100'
                        }`}>
                          {slot.status}
                        </span>
                      </td>
                      <td className="py-3 text-gray-400 text-sm">
                        {new Date(slot.timestamp).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Yield Page
  const YieldPage = () => (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Yield Farming</h1>
          <p className="text-gray-400">Earn rewards by providing liquidity to our pools</p>
        </div>

        {/* Pool Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-white mb-1">$4.2M</div>
            <div className="text-gray-400 text-sm">Total Value Locked</div>
          </div>
          <div className="bg-gray-800 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-green-400 mb-1">18.5%</div>
            <div className="text-gray-400 text-sm">Average APY</div>
          </div>
          <div className="bg-gray-800 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-purple-400 mb-1">3</div>
            <div className="text-gray-400 text-sm">Active Pools</div>
          </div>
        </div>

        {/* Liquidity Pools */}
        <div className="bg-gray-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-6">Available Pools</h2>
          
          <div className="space-y-4">
            {LIQUIDITY_POOLS.map((pool) => (
              <div key={pool.id} className="bg-gray-700 rounded-lg p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="flex items-center space-x-4 mb-4 md:mb-0">
                    <div className="flex -space-x-2">
                      <div className="w-10 h-10 bg-blue-500 rounded-full border-2 border-gray-700 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">ETH</span>
                      </div>
                      <div className="w-10 h-10 bg-purple-500 rounded-full border-2 border-gray-700 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">FIB</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-white font-bold text-lg">{pool.pair}</div>
                      <div className="text-gray-400 text-sm">Rewards: {pool.rewards}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-green-400 font-bold text-lg">{pool.apy}</div>
                      <div className="text-gray-400 text-sm">APY</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white font-medium">{pool.tvl}</div>
                      <div className="text-gray-400 text-sm">TVL</div>
                    </div>
                    <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                      Add Liquidity
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Main App Component
  if (!isWalletConnected || !hasFIBTokens) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Navigation />
        <HomePage />
        {notifications.map((notification) => (
          <NotificationToast key={notification.id} notification={notification} />
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation />
      
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'slotting' && <SlottingPage />}
      {currentPage === 'wallet' && <WalletPage />}
      {currentPage === 'yield' && <YieldPage />}
      
      {notifications.map((notification) => (
        <NotificationToast key={notification.id} notification={notification} />
      ))}
    </div>
  );
};

export default FlibberApp;